import { ChangeEvent, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { z } from 'zod';
import { ContentGroup } from '@rossum/components/content-group/ContentGroup';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { DatasetAPISchema } from '@rossum/mdh-api-client';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@rossum/ui/material';
import TextFieldControl from '../../../components/ReactHookForm/controls/TextFieldControl';
import { mdhUrl } from '../../../constants/config';
import { link } from '../../../lib/formaterValues';
import { notify } from '../../toaster';
import { DatasetsHeader } from '../header';
import { useUploadDataset } from '../hooks/useUploadDataset';
import { datasetTablePath } from '../routes';
import { pageSpacingStyles } from '../styles';
import { datasetNamePattern, generateDatasetName } from './generateDatasetName';
import { UploadArea } from './UploadArea';

const maxFileSize = 52428800; // 50 MB
const FORM_ID = 'dataset-import';

// TODO: Cleanup the whole file
export const DatasetImport = () => {
  const intl = useIntl();
  const [file, setFile] = useState<File | null>(null);

  const { control, handleSubmit, getValues, setValue, trigger } = useForm({
    defaultValues: {
      name: '',
      metadata: {
        manual: true,
        name: '',
        description: '',
      },
      // Do we actually need encoding in 2025?
      encoding: 'utf-8',
    },
    resolver: zodResolver(
      z.object({
        name: z
          .string()
          .min(1, {
            message: intl.formatMessage({
              id: 'features.datasets.settings.error.required',
            }),
          })
          .regex(datasetNamePattern, {
            message: intl.formatMessage({
              id: 'features.datasets.settings.form.idSection.id.error.invalid',
            }),
          }),
        metadata: z
          .object({
            name: z.string().min(1, {
              message: intl.formatMessage({
                id: 'features.datasets.settings.error.required',
              }),
            }),
            description: z.string(),
            manual: z.boolean(),
          })
          .partial()
          .default({}),
        encoding: z.string(),
      })
    ),
  });

  const { push } = useHistory();

  const { mutate: uploadDataset, isLoading: isUploading } = useUploadDataset();

  const onSubmit: SubmitHandler<{
    name: string;
    metadata: { name: string; description: string; manual?: boolean };
    encoding: string;
  }> = form => {
    if (file !== null) {
      uploadDataset(
        {
          name: form.name,
          file,
          metadata: form.metadata,
          encoding: form.encoding,
          dynamic: true,
        },
        {
          onError: () => {
            notify.error({
              title: intl.formatMessage({
                id: 'features.datasets.notification.importFailed',
              }),
            });
          },
          onSuccess: (table: DatasetAPISchema) => {
            push(datasetTablePath(table.name));
            notify.default({
              title: intl.formatMessage({
                id: 'features.datasets.notification.importStarted',
              }),
            });
          },
        }
      );
    }
  };

  return (
    <PageLayout
      renderHeader={props => (
        <DatasetsHeader
          {...props}
          datasetId={undefined}
          datasetTab={undefined}
          title={intl.formatMessage({
            id: 'features.datasets.uploadDataset.title',
          })}
          description={intl.formatMessage({
            id: 'features.datasets.uploadDataset.description',
          })}
          breadcrumbs={[
            {
              label: intl.formatMessage({
                id: 'features.datasets.createNew.title',
              }),
            },
          ]}
          buttons={[
            <Button
              key="save-dataset-button"
              variant="contained"
              type="submit"
              form={FORM_ID}
              disabled={isUploading || file === null}
              endIcon={isUploading ? <CircularProgress size={14} /> : null}
            >
              {intl.formatMessage({
                id: 'features.datasets.settings.header.button.save',
              })}
            </Button>,
          ]}
        />
      )}
    >
      <Stack
        component="form"
        sx={pageSpacingStyles}
        gap={2}
        onSubmit={handleSubmit(onSubmit)}
        id={FORM_ID}
      >
        <ContentGroup
          title={intl.formatMessage({
            id: 'features.datasets.settings.form.idSection.title',
          })}
          description={
            <Stack spacing={1}>
              <Typography variant="inherit">
                {intl.formatMessage({
                  id: 'features.datasets.settings.form.idSection.description',
                })}
              </Typography>
              {/* TODO: Link */}
            </Stack>
          }
        >
          <Stack spacing={2}>
            <TextFieldControl
              ControllerProps={{ control, name: 'metadata.name' }}
              label={intl.formatMessage({
                id: 'features.datasets.settings.form.idSection.name.label',
              })}
              FieldLabelProps={{
                layout: 'floating',
              }}
              onBlur={() => {
                if (getValues('name') === '') {
                  setValue(
                    'name',
                    generateDatasetName(getValues('metadata.name'))
                  );
                  trigger('name');
                }
              }}
            />

            <TextFieldControl
              ControllerProps={{ control, name: 'name' }}
              label={intl.formatMessage({
                id: 'features.datasets.settings.form.idSection.id.label',
              })}
              helperText={intl.formatMessage({
                id: 'features.datasets.settings.form.idSection.id.helperText',
              })}
              FieldLabelProps={{
                layout: 'floating',
              }}
            />

            <TextFieldControl
              ControllerProps={{ control, name: 'metadata.description' }}
              label={intl.formatMessage({
                id: 'features.datasets.settings.form.idSection.description.label',
              })}
              FieldLabelProps={{
                layout: 'floating',
              }}
              multiline
              maxRows={3}
            />
          </Stack>

          <FormControlLabel
            sx={{ pl: 2 }}
            control={
              <Controller
                control={control}
                name="metadata.manual"
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    checked={field.value}
                    size="small"
                    edge="start"
                  />
                )}
              />
            }
            label={intl.formatMessage({
              id: 'features.datasets.settings.form.idSection.manual.label',
            })}
          />
        </ContentGroup>
        <ContentGroup
          title={intl.formatMessage({
            id: 'features.datasets.uploadFile.title',
          })}
          description={
            <Stack spacing={1}>
              <Typography variant="inherit">
                {intl.formatMessage({
                  id: 'features.datasets.uploadFile.description',
                })}
              </Typography>
              <Typography variant="inherit">
                {intl.formatMessage(
                  { id: 'features.datasets.uploadFile.docs' },
                  {
                    link: link(
                      `${mdhUrl}/svc/master-data-hub/api/docs#tag/Dataset`
                    ),
                  }
                )}
              </Typography>
            </Stack>
          }
        >
          <UploadArea
            file={file}
            onUploadChange={(e: ChangeEvent<HTMLInputElement>) => {
              const selectedFile = e.target.files?.[0];
              if (selectedFile) {
                if (selectedFile.size > maxFileSize) {
                  // return setFileSizeError(true);
                }
                // setFileSizeError(false);
                return setFile(selectedFile);
              }
              return setFile(null);
            }}
          />
        </ContentGroup>
      </Stack>
    </PageLayout>
  );
};
